import { NoiseEvent } from 'app/business-logic/services/noise-service';
import Guid from 'core/types/Guid';
import { DateTime } from 'luxon';
import { useAlertById } from '../../../hooks';
import { useAlertConfiguration } from '../../../hooks/use-alert-configuration';
import { useEventsByAlertId } from '../../events-listing/useEventsByAlertId';
import { useTraceVariableIds } from './useTraceVariableIds';

export const useConfigData = (alertId: Guid) => {
  const { data: alert, isFetching: isAlertLoading } = useAlertById(alertId);
  const { alertScriptConfigurationId } = alert ?? {};
  const alertEventId = alert?.id;
  const { data: config, isFetching: isAlertConfigLoading } = useAlertConfiguration(alertScriptConfigurationId);
  const { data: traceVariableIds, isFetching: isTraceVariablesLoading } =
    useTraceVariableIds(alertScriptConfigurationId);
  const { data: events, isFetching: isEventsLoading } = useEventsByAlertId(alertEventId);
  const { traceWindowLeadInSeconds, traceWindowLeadOutSeconds, omnisLocationId, omnisDataType, variables } =
    config ?? {};

  const dateRange = getEventsChartDateRage({
    events,
    alert,
    traceWindowLeadInSeconds,
    traceWindowLeadOutSeconds,
  });
  const { utcEndDate, utcStartDate } = dateRange ?? {};
  return {
    traceVariableIds,
    omnisLocationId,
    omnisDataType,
    utcEndDate: utcEndDate ?? undefined,
    utcStartDate: utcStartDate ?? undefined,
    events,
    isLoading: isAlertLoading || isAlertConfigLoading || isTraceVariablesLoading || isEventsLoading,
    variables,
  };
};

function getEventsChartDateRage({
  events,
  alert,
  traceWindowLeadInSeconds,
  traceWindowLeadOutSeconds,
}: {
  events?: NoiseEvent[] | null;
  alert?: ReturnType<typeof useAlertById>['data'];
  traceWindowLeadInSeconds?: number | null;
  traceWindowLeadOutSeconds?: number | null;
}) {
  if (!alert) return;

  if (typeof traceWindowLeadInSeconds !== 'number' || typeof traceWindowLeadOutSeconds !== 'number') return;

  const { utcTriggeredTime } = alert;

  const earliestAlertPeriod = DateTime.fromISO(utcTriggeredTime).toUTC().minus({ hours: 12 }).toMillis();
  const latestAlertPeriod = DateTime.fromISO(utcTriggeredTime).toUTC().plus({ hours: 12 }).toMillis();

  const eventTimes = events
    ?.flatMap(event => {
      const startTime = event.startTime ? DateTime.fromISO(event.startTime).toUTC() : undefined;
      const endTime = event.endTime ? DateTime.fromISO(event.endTime).toUTC() : undefined;
      if (!startTime || !endTime) return [];
      return [startTime, endTime];
    })
    .sort((a, b) => {
      if (a < b) return -1;
      if (a > b) return 1;
      return 0;
    });

  const earliestEventTime = eventTimes?.at(0);
  const latestEventTime = eventTimes?.at(-1);

  // Events occurring 12 hours before or after the alert triggered time are invalid
  const eventsAreInvalid =
    events?.some(event => {
      const eventStartTime = event.startTime ? DateTime.fromISO(event.startTime).toUTC().toMillis() : undefined;
      const eventEndTime = event.endTime ? DateTime.fromISO(event.endTime).toUTC().toMillis() : undefined;
      if (!eventStartTime || !eventEndTime) return false;
      return eventStartTime > latestAlertPeriod || eventEndTime < earliestAlertPeriod;
    }) ||
    !earliestEventTime ||
    !latestEventTime;

  // If the events are invalid, the chart should display the period from (alert triggered time - lead in seconds) to (alert triggered time + lead out seconds).
  if (eventsAreInvalid) {
    const utcStartDate = DateTime.fromISO(utcTriggeredTime)
      .toUTC()
      .minus({ seconds: traceWindowLeadInSeconds })
      .toISO();
    const utcEndDate = DateTime.fromISO(utcTriggeredTime).toUTC().plus({ seconds: traceWindowLeadOutSeconds }).toISO();
    return {
      utcStartDate,
      utcEndDate,
    };
  } else {
    // If the events are valid the chart should display the period from (earliest event time - lead in seconds) to (latest event time + lead out seconds).
    const utcStartDate = earliestEventTime.minus({ seconds: traceWindowLeadInSeconds }).toISO();
    const utcEndDate = latestEventTime.plus({ seconds: traceWindowLeadOutSeconds }).toISO();

    return {
      utcStartDate,
      utcEndDate,
    };
  }
}
